import { useInfiniteQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrEvent } from '@nostrify/nostrify';
import { useFollowList, FEED_LENS_PUBKEY } from '@/hooks/useFollowList';

const PAGE_SIZE = 20;
const QUERY_TIMEOUT_MS = 4000;
/** Over-fetch a little because we filter out replies client-side. */
const OVER_FETCH_MULTIPLIER = 2;
/** If an event claims to be wildly newer than the rest, ignore it as an
 *  out-of-sync relay outlier when computing the pagination cursor. */
const CURSOR_OUTLIER_THRESHOLD_S = 6 * 60 * 60; // 6h

export interface FeedPage {
  events: NostrEvent[];
  /** Oldest real timestamp in the page, used as `until` for the next page. */
  oldestTimestamp: number;
  rawCount: number;
}

/**
 * Returns true if this kind 1 event is a reply (has an `e` tag referencing
 * another event). We want only top-level posts in the timeline.
 */
function isReply(event: NostrEvent): boolean {
  return event.tags.some(([name]) => name === 'e' || name === 'E' || name === 'q');
}

/**
 * Timestamp cursor helper that ignores a single huge outlier (caused by
 * relays with drifting clocks) so pagination doesn't skip hours of content.
 */
function getPaginationCursor(events: NostrEvent[]): number {
  if (events.length === 0) return 0;
  const sorted = [...events].sort((a, b) => a.created_at - b.created_at);
  const oldest = sorted[0].created_at;
  // If the second-oldest is > threshold newer than the oldest, and the rest
  // form a tight cluster, the oldest may be a late relay delivery of a VERY
  // old event. Prefer the cluster start.
  if (sorted.length > 1) {
    const secondOldest = sorted[1].created_at;
    if (secondOldest - oldest > CURSOR_OUTLIER_THRESHOLD_S) {
      return secondOldest;
    }
  }
  return oldest;
}

/**
 * Main "follows" feed: infinite-scrolling list of kind 1 notes from every
 * account the hardcoded lens account follows (plus the lens account itself).
 *
 * Pagination is cursor-based via the `until` filter field, following the
 * Ditto pattern described in project docs.
 */
export function useFeed() {
  const { nostr } = useNostr();
  const follows = useFollowList();

  const followsReady = follows.isSuccess;
  const followPubkeys = follows.data?.pubkeys ?? [];

  return useInfiniteQuery<FeedPage>({
    queryKey: ['feed', 'follows', FEED_LENS_PUBKEY, followPubkeys.length],
    enabled: followsReady,
    initialPageParam: 0,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async ({ pageParam, signal }) => {
      const until = typeof pageParam === 'number' && pageParam > 0
        ? pageParam
        : undefined;

      const authors = followPubkeys.length > 0
        ? [...new Set([...followPubkeys, FEED_LENS_PUBKEY])]
        : [FEED_LENS_PUBKEY];

      const filter: {
        kinds: number[];
        authors: string[];
        limit: number;
        until?: number;
      } = {
        kinds: [1],
        authors,
        limit: PAGE_SIZE * OVER_FETCH_MULTIPLIER,
      };

      if (until) filter.until = until - 1;

      const rawEvents = await nostr.query(
        [filter],
        { signal: AbortSignal.any([signal, AbortSignal.timeout(QUERY_TIMEOUT_MS)]) },
      );

      const events = rawEvents
        .filter((e) => e.kind === 1)
        .filter((e) => !isReply(e))
        .sort((a, b) => b.created_at - a.created_at);

      return {
        events,
        oldestTimestamp: getPaginationCursor(rawEvents),
        rawCount: rawEvents.length,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.rawCount === 0) return undefined;
      if (!lastPage.oldestTimestamp) return undefined;
      return lastPage.oldestTimestamp;
    },
  });
}
